import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/auth/SessionContext";
import { api } from "@/lib/api";
import { focusFirstError } from "@/lib/formA11y";
import { auditActionLabel, formatWhen, lifecycleLabel, roleLabel } from "@/lib/format";
import { ApiClientError, type AccountSession, type AuditPage } from "@/lib/types";
import { Button } from "@/components/Button";
import { ButtonLink } from "@/components/ButtonLink";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { Field, PasswordInput, TextInput } from "@/components/Field";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { StatePanel } from "@/components/StatePanel";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";

export function AccountPage() {
  const { session, applySession, clearSession } = useSession();
  const { m, locale, fmt } = useLocale();
  const navigate = useNavigate();
  const canRenameOrg = session?.user.role === "owner" || session?.user.role === "admin";
  const roleDisplay = session ? roleLabel(m, session.user.role) : "";

  const [name, setName] = useState(session?.user.name ?? "");
  const [organization, setOrganization] = useState(session?.tenant.name ?? "");
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileOk, setProfileOk] = useState<string | null>(null);
  const [profileBusy, setProfileBusy] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formOk, setFormOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);

  const [sessions, setSessions] = useState<AccountSession[] | null>(null);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [sessionMsg, setSessionMsg] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AccountSession | null>(null);
  const [revokeOthersOpen, setRevokeOthersOpen] = useState(false);
  const [sessionBusy, setSessionBusy] = useState(false);

  const [audit, setAudit] = useState<AuditPage | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    setName(session.user.name);
    setOrganization(session.tenant.name);
  }, [session]);

  useEffect(() => {
    void api
      .audit()
      .then(setAudit)
      .catch((e) => setAuditError(e instanceof Error ? e.message : m.account.auditUnavailable));
    void api
      .sessions()
      .then((res) => {
        setSessions(res.items);
        setSessionsError(null);
      })
      .catch((e) => setSessionsError(e instanceof Error ? e.message : m.account.sessionsFailed));
  }, [m.account.auditUnavailable, m.account.sessionsFailed]);

  async function onProfile(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    const trimmedName = name.trim();
    const trimmedOrg = organization.trim();
    if (trimmedName.length < 2 || trimmedName.length > 100) next["profile-name"] = m.account.nameRequired;
    if (canRenameOrg && (trimmedOrg.length < 2 || trimmedOrg.length > 100)) {
      next["profile-org"] = m.account.orgRequired;
    }
    setProfileErrors(next);
    if (Object.keys(next).length) {
      queueMicrotask(() => focusFirstError(next, ["profile-name", "profile-org"]));
      return;
    }
    setProfileBusy(true);
    setProfileError(null);
    setProfileOk(null);
    try {
      const payload = canRenameOrg
        ? await api.updateProfile({ name: trimmedName, organization: trimmedOrg })
        : await api.updateProfile({ name: trimmedName });
      applySession(payload);
      setProfileOk(m.account.profileSaved);
    } catch (err) {
      setProfileError(err instanceof ApiClientError ? err.message : m.account.profileFailed);
    } finally {
      setProfileBusy(false);
    }
  }

  async function onPassword(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!currentPassword) next.cur = m.account.currentRequired;
    if (newPassword.length < 12) next.next = m.account.newMin;
    setFieldErrors(next);
    if (Object.keys(next).length) {
      queueMicrotask(() => focusFirstError(next, ["cur", "next"]));
      return;
    }
    setBusy(true);
    setFormError(null);
    setFormOk(null);
    try {
      await api.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setFormOk(m.account.changed);
      const realPayload = await api.session();
      applySession(realPayload);
      const res = await api.sessions();
      setSessions(res.items);
    } catch (err) {
      setFormError(err instanceof ApiClientError ? err.message : m.account.changeFailed);
    } finally {
      setBusy(false);
    }
  }

  async function onRevokeOne() {
    if (!revokeTarget) return;
    setSessionBusy(true);
    setSessionMsg(null);
    try {
      await api.revokeSession(revokeTarget.id);
      setRevokeTarget(null);
      const res = await api.sessions();
      setSessions(res.items);
    } catch (err) {
      setSessionMsg(err instanceof Error ? err.message : m.account.revokeFailed);
    } finally {
      setSessionBusy(false);
    }
  }

  async function onRevokeOthers() {
    setSessionBusy(true);
    setSessionMsg(null);
    try {
      const res = await api.revokeOtherSessions();
      setRevokeOthersOpen(false);
      setSessionMsg(fmt(m.account.revokedCount, { n: res.revoked }));
      const list = await api.sessions();
      setSessions(list.items);
    } catch (err) {
      setSessionMsg(err instanceof Error ? err.message : m.account.revokeFailed);
    } finally {
      setSessionBusy(false);
    }
  }

  async function onLogout() {
    setLogoutBusy(true);
    try {
      await api.logout();
    } catch {
      // still clear local session shell
    } finally {
      clearSession();
      navigate("/login");
      setLogoutBusy(false);
    }
  }

  if (!session) {
    return (
      <StatePanel
        kind="unauthenticated"
        title={m.states.sessionEnded}
        message={m.states.sessionEndedBody}
        actionLabel={m.common.signIn}
        onAction={() => navigate("/login")}
      />
    );
  }

  return (
    <div>
      <h1 className={shell.pageTitle}>{m.account.title}</h1>
      <p className={shell.pageLead}>
        {session.user.name} · {session.user.email} · {roleDisplay}
      </p>

      <form
        noValidate
        onSubmit={onProfile}
        className={ui.panel}
        style={{ display: "grid", gap: "0.75rem", marginBottom: "1rem" }}
      >
        <h2 className="display" style={{ fontSize: "1.1rem", margin: 0 }}>
          {m.account.profileTitle}
        </h2>
        <p className={ui.meta} style={{ margin: 0 }}>
          {m.account.profileLead}
        </p>
        <Field label={m.account.name} htmlFor="profile-name" error={profileErrors["profile-name"]}>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
          />
        </Field>
        <Field label={m.account.emailReadonly} htmlFor="profile-email">
          <TextInput id="profile-email" value={session.user.email} readOnly disabled />
        </Field>
        <Field label={m.account.roleReadonly} htmlFor="profile-role">
          <TextInput id="profile-role" value={roleDisplay} readOnly disabled />
        </Field>
        <Field
          label={m.account.organization}
          htmlFor="profile-org"
          error={profileErrors["profile-org"]}
          hint={canRenameOrg ? m.account.orgRenameHint : m.account.orgRenameForbidden}
        >
          <TextInput
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            autoComplete="organization"
            readOnly={!canRenameOrg}
            disabled={!canRenameOrg}
          />
        </Field>
        {profileError ? (
          <p className={ui.fieldError} role="alert">
            {profileError}
          </p>
        ) : null}
        {profileOk ? <p className={ui.meta}>{profileOk}</p> : null}
        <Button type="submit" disabled={profileBusy} aria-busy={profileBusy || undefined}>
          {profileBusy ? m.common.saving : m.account.saveProfile}
        </Button>
        <div>
          <LanguageSwitch />
        </div>
      </form>

      <form
        noValidate
        onSubmit={onPassword}
        className={ui.panel}
        style={{ display: "grid", gap: "0.75rem", marginBottom: "1rem" }}
      >
        <h2 className="display" style={{ fontSize: "1.1rem", margin: 0 }}>
          {m.account.changePassword}
        </h2>
        <Field label={m.account.currentPassword} htmlFor="cur" error={fieldErrors.cur}>
          <PasswordInput
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </Field>
        <Field
          label={m.account.newPassword}
          htmlFor="next"
          error={fieldErrors.next}
          hint={m.auth.passwordMin}
        >
          <PasswordInput
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </Field>
        {formError ? (
          <p className={ui.fieldError} role="alert">
            {formError}
          </p>
        ) : null}
        {formOk ? <p className={ui.meta}>{formOk}</p> : null}
        <Button type="submit" disabled={busy} aria-busy={busy || undefined}>
          {busy ? m.common.saving : m.account.changePassword}
        </Button>
      </form>

      <div className={ui.panel} style={{ marginBottom: "1rem" }}>
        <h2 className="display" style={{ fontSize: "1.1rem", marginTop: 0 }}>
          {m.account.sessionsTitle}
        </h2>
        <p className={ui.meta}>{m.account.sessionsLead}</p>
        {sessionsError ? <p className={ui.fieldError}>{sessionsError}</p> : null}
        {sessionMsg ? <p className={ui.meta}>{sessionMsg}</p> : null}
        {!sessions && !sessionsError ? <p className={ui.meta}>{m.common.loading}</p> : null}
        {sessions && sessions.length === 0 ? <p className={ui.meta}>{m.account.sessionsEmpty}</p> : null}
        {sessions?.map((item) => (
          <div
            key={item.id}
            style={{ padding: "0.55rem 0", borderBottom: "1px solid var(--color-line)" }}
          >
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
              <span className="mono">{item.id.slice(0, 8)}…</span>
              {item.current ? <span className={ui.chip}>{m.account.sessionCurrent}</span> : null}
            </div>
            <div className={ui.meta}>
              {m.account.sessionCreated} {formatWhen(item.created_at, locale)} · {m.account.sessionExpires}{" "}
              {formatWhen(item.expires_at, locale)}
            </div>
            {!item.current ? (
              <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.4rem" }}>
                <Button variant="danger" onClick={() => setRevokeTarget(item)}>
                  {m.account.revokeSession}
                </Button>
              </div>
            ) : null}
          </div>
        ))}
        <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
          <Button variant="secondary" onClick={() => setRevokeOthersOpen(true)}>
            {m.account.revokeOthers}
          </Button>
        </div>
      </div>

      <div className={ui.panel} style={{ marginBottom: "1rem" }}>
        <h2 className="display" style={{ fontSize: "1.1rem", marginTop: 0 }}>
          {m.account.audit}
        </h2>
        {auditError ? <p className={ui.fieldError}>{auditError}</p> : null}
        {!audit && !auditError ? <p className={ui.meta}>{m.common.loading}</p> : null}
        {audit && audit.items.length === 0 ? <p className={ui.meta}>{m.account.auditEmpty}</p> : null}
        {audit?.items.map((item) => (
          <div key={item.id} style={{ padding: "0.45rem 0", borderBottom: "1px solid var(--color-line)" }}>
            <div>{auditActionLabel(m, item.action)}</div>
            <div className={`mono ${ui.meta}`}>{formatWhen(item.created_at, locale)}</div>
          </div>
        ))}
      </div>

      <div className={ui.panel} style={{ marginBottom: "1rem" }}>
        <h2 className="display" style={{ fontSize: "1.1rem", marginTop: 0 }}>
          {m.account.connections}
        </h2>
        <p className={ui.meta} style={{ marginTop: 0 }}>
          {m.account.connectionsLead}
        </p>
        <div className={ui.actions} style={{ justifyContent: "flex-start" }}>
          <ButtonLink to="/app/integrations" variant="secondary">
            {m.account.openConnections}
          </ButtonLink>
          <ButtonLink to="/app/environment" variant="secondary">
            {m.account.openEnvironment}
          </ButtonLink>
        </div>
      </div>

      <div className={ui.actions} style={{ justifyContent: "flex-start" }}>
        <Button variant="secondary" disabled={logoutBusy} onClick={() => void onLogout()}>
          {logoutBusy ? m.common.loading : m.common.signOut}
        </Button>
        <ButtonLink to="/privacy" variant="ghost">
          {m.common.privacy}
        </ButtonLink>
        <ButtonLink to="/terms" variant="ghost">
          {m.common.terms}
        </ButtonLink>
      </div>
      <p className={ui.meta} style={{ marginTop: "1rem" }}>
        {m.account.pilotMeta} · {lifecycleLabel(m, "pilot")}
      </p>

      <ConfirmSheet
        open={Boolean(revokeTarget)}
        title={m.account.revokeConfirmTitle}
        confirmLabel={m.account.revokeSession}
        danger
        busy={sessionBusy}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => void onRevokeOne()}
      >
        <p>{m.account.revokeConfirmBody}</p>
      </ConfirmSheet>

      <ConfirmSheet
        open={revokeOthersOpen}
        title={m.account.revokeOthersTitle}
        confirmLabel={m.account.revokeOthersConfirm}
        danger
        busy={sessionBusy}
        onClose={() => setRevokeOthersOpen(false)}
        onConfirm={() => void onRevokeOthers()}
      >
        <p>{m.account.revokeOthersBody}</p>
      </ConfirmSheet>
    </div>
  );
}
